﻿import { arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, offset, shift, useFloating, useMergeRefs } from '@floating-ui/react';
import { Slider, SliderHandle, SliderProps } from '@progress/kendo-react-inputs';
import React from 'react';
import './SliderWithTooltip.css';

export const SliderWithTooltip = React.forwardRef<SliderHandle, SliderProps>((props, forwardedRef) => {
    const sliderRef = React.useRef<SliderHandle>(null);
    const mergedRef = useMergeRefs([sliderRef, forwardedRef]);
    const arrowRef = React.useRef<SVGSVGElement>(null);

    const [isOpen, setIsOpen] = React.useState(false);

    const { refs, floatingStyles, isPositioned, context } = useFloating({
        open: isOpen,
        placement: 'top',
        strategy: 'fixed',
        middleware: [offset(10), flip(), shift({ padding: 4 }), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate
    });

    const { setReference } = refs;

    React.useEffect(() => {
        const dragHandle = sliderRef.current?.sliderTrack?.querySelector<HTMLElement>('.k-draghandle');
        if (!dragHandle) {
            return;
        }

        setReference(dragHandle);
        dragHandle.removeAttribute('title');

        let dragging = false;

        const onPointerEnter = () => setIsOpen(true);
        const onPointerLeave = () => {
            if (!dragging) {
                setIsOpen(false);
            }
        };
        const onPointerDown = () => {
            dragging = true;
            setIsOpen(true);
        };
        const onPointerUp = () => {
            dragging = false;
            setIsOpen(false);
        };

        dragHandle.addEventListener('pointerenter', onPointerEnter);
        dragHandle.addEventListener('pointerleave', onPointerLeave);
        dragHandle.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('pointerup', onPointerUp);

        return () => {
            dragHandle.removeEventListener('pointerenter', onPointerEnter);
            dragHandle.removeEventListener('pointerleave', onPointerLeave);
            dragHandle.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('pointerup', onPointerUp);
            setReference(null);
        };
    }, [setReference]);

    return (
        <>
            <Slider ref={mergedRef} {...props} />
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{ ...floatingStyles, visibility: isPositioned ? 'visible' : 'hidden' }}
                        className="slider-value-tooltip">
                        {!Number.isInteger(props.value) ? props.value?.toFixed(2) : props.value}
                        <FloatingArrow ref={arrowRef} context={context} className="slider-value-tooltip-arrow" />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
});

SliderWithTooltip.displayName = 'SliderWithTooltip';
